/**
 * Community listings list with filters and cards.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { communityListingsService } from '../services/communityListingsService';
import { LISTING_CATEGORIES, LISTING_CONDITIONS } from '../constants/communityListings';
import { PlaceSearchInput } from '../../../components/ui/PlaceSearchInput';
import { CITY_TYPES } from '../../../services/geocodingService';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { filterListings } from '../campEssentialsFilters';
import './CommunityListings.css';
import './camp-essentials-gear.css';

const RADIUS_CHOICES = [10, 25, 50, 75, 100];
const DEFAULT_RADIUS = 50;

const LISTING_STATUS_LABEL = {
  active: 'Active',
  sold: 'Sold',
  removed: 'Removed',
};

const CONDITION_LABEL = Object.fromEntries(LISTING_CONDITIONS.map((c) => [c.value, c.label]));

function parseListResponse(data) {
  if (data && typeof data.results !== 'undefined') return data.results;
  return Array.isArray(data) ? data : [];
}

function ListingCard({ listing, currentUserId, showStatusBadge, mockup }) {
  const navigate = useNavigate();
  const isAuthor = currentUserId != null && Number(listing.author_id) === Number(currentUserId);
  const statusKey = listing.status || 'active';
  const statusLabel = LISTING_STATUS_LABEL[statusKey] || statusKey;
  const daysLeft = listing.days_until_auto_removal;
  const priceStr =
    listing.price != null && listing.price !== ''
      ? `$${Number(listing.price).toLocaleString()}`
      : null;
  const conditionLabel =
    listing.condition_display || CONDITION_LABEL[listing.condition] || listing.condition;

  return (
    <div
      className={`community-listing-card${mockup ? ' community-listing-card--mockup' : ''}`}
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/camp-essentials/gear/listings/${listing.id}`)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          navigate(`/camp-essentials/gear/listings/${listing.id}`);
        }
      }}
      aria-label={`View listing: ${listing.title}`}
    >
      <div className="community-listing-card__thumb">
        {listing.thumbnail_url ? (
          <img src={listing.thumbnail_url} alt="" />
        ) : (
          <div className="community-listing-card__thumb-placeholder" aria-hidden="true" />
        )}
        {mockup && priceStr ? (
          <span className="community-listing-card__price-overlay">{priceStr}</span>
        ) : null}
        {mockup && conditionLabel ? (
          <span className="community-listing-card__condition-overlay">{conditionLabel}</span>
        ) : null}
      </div>
      <div className="community-listing-card__body">
        <div className="community-listing-card__title">{listing.title}</div>
        <div className="community-listing-card__meta">
          {listing.category_display && (
            <span className="community-listing-card__category">{listing.category_display}</span>
          )}
          {!mockup && priceStr && <span className="community-listing-card__price">{priceStr}</span>}
        </div>
        {(listing.location_text || listing.distance_miles != null) && (
          <div className="community-listing-card__location text-muted small">
            {listing.location_text || ''}
            {listing.location_text && listing.distance_miles != null ? ' · ' : ''}
            {listing.distance_miles != null ? `${listing.distance_miles} mi away` : ''}
          </div>
        )}
        {daysLeft != null && listing.status === 'active' && (
          <div className="community-listing-card__days small">
            {daysLeft === 0 ? 'Removes today' : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`}
          </div>
        )}
        {showStatusBadge && statusKey !== 'active' && (
          <span className="badge bg-warning text-dark community-listing-card__badge">{statusLabel}</span>
        )}
        {isAuthor && !showStatusBadge && (
          <span className="badge bg-secondary community-listing-card__badge">Yours</span>
        )}
      </div>
    </div>
  );
}

export function CommunityListingsList({
  embedded = false,
  searchQuery = '',
  categoryFilter = '',
  mockupCards = false,
}) {
  const navigate = useNavigate();
  const { currentUser, loading: userLoading } = useCurrentUser();
  const currentUserId = currentUser?.id ?? null;
  const [searchParams, setSearchParams] = useSearchParams();
  const mineOnly = searchParams.get('mine') === '1';

  const setMineOnly = (nextMine) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set('tab', 'listings');
        if (nextMine) p.set('mine', '1');
        else p.delete('mine');
        return p;
      },
      { replace: true }
    );
  };

  useEffect(() => {
    if (userLoading || !mineOnly || currentUserId != null) return;
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.delete('mine');
        return p;
      },
      { replace: true }
    );
  }, [userLoading, mineOnly, currentUserId, setSearchParams]);

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [category, setCategory] = useState('');
  const [keyword, setKeyword] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationQuery, setLocationQuery] = useState('');
  const [radius, setRadius] = useState(DEFAULT_RADIUS);
  const [locationOpen, setLocationOpen] = useState(false);

  const effectiveCategory = embedded ? categoryFilter : category;
  const effectiveKeyword = embedded ? searchQuery : keyword;

  const fetchListings = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (mineOnly) params.mine = '1';
      if (!embedded && effectiveCategory) params.category = effectiveCategory;
      if (!embedded && effectiveKeyword.trim()) params.keyword = effectiveKeyword.trim();
      if (selectedLocation?.latitude != null && selectedLocation?.longitude != null) {
        params.latitude = selectedLocation.latitude;
        params.longitude = selectedLocation.longitude;
        params.radius_miles = radius;
      }
      const data = await communityListingsService.list(params);
      setListings(parseListResponse(data));
    } catch (e) {
      setListings([]);
      setError('Unable to load listings. Try again.');
    } finally {
      setLoading(false);
    }
  }, [embedded, effectiveCategory, effectiveKeyword, selectedLocation, radius, mineOnly]);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  const visibleListings = useMemo(() => {
    if (!embedded) return listings;
    return filterListings(listings, {
      searchQuery: effectiveKeyword,
      category: effectiveCategory,
    });
  }, [embedded, listings, effectiveKeyword, effectiveCategory]);

  const stats = useMemo(() => {
    const active = visibleListings.filter((l) => (l.status || 'active') === 'active');
    const priced = active.filter((l) => l.price != null && l.price !== '');
    const avgPrice =
      priced.length > 0
        ? priced.reduce((sum, l) => sum + Number(l.price), 0) / priced.length
        : null;
    return {
      active: active.length,
      priced: priced.length,
      avgPrice,
    };
  }, [visibleListings]);

  return (
    <div
      className={`community-listings-list${embedded ? ' community-listings-list--embedded' : ''}`}
    >
      {!embedded && (
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <h2 className="h6 mb-0">Community listings</h2>
          <button
            type="button"
            className="y-btn y-btn-primary y-btn-sm"
            onClick={() => navigate('/camp-essentials/gear/listings/new')}
          >
            Post a listing
          </button>
        </div>
      )}

      {embedded && visibleListings.length > 0 ? (
        <div className="camp-essentials-gear__market-stats" aria-label="Marketplace summary">
          <div className="camp-essentials-gear__stat">
            <span className="camp-essentials-gear__stat-label">Active listings</span>
            <p className="camp-essentials-gear__stat-value">{stats.active}</p>
          </div>
          <div className="camp-essentials-gear__stat">
            <span className="camp-essentials-gear__stat-label">With price</span>
            <p className="camp-essentials-gear__stat-value">{stats.priced}</p>
          </div>
          <div className="camp-essentials-gear__stat">
            <span className="camp-essentials-gear__stat-label">Avg price</span>
            <p className="camp-essentials-gear__stat-value">
              {stats.avgPrice != null ? `$${Math.round(stats.avgPrice).toLocaleString()}` : '—'}
            </p>
          </div>
          <div className="camp-essentials-gear__stat">
            <span className="camp-essentials-gear__stat-label">In view</span>
            <p className="camp-essentials-gear__stat-value">{visibleListings.length}</p>
          </div>
        </div>
      ) : null}

      {currentUserId != null && (
        <div
          className="btn-group btn-group-sm community-listings-scope mb-3"
          role="group"
          aria-label="Listing scope"
        >
          <button
            type="button"
            className={`btn btn-outline-secondary ${!mineOnly ? 'active' : ''}`}
            onClick={() => setMineOnly(false)}
            aria-pressed={!mineOnly}
          >
            Browse all
          </button>
          <button
            type="button"
            className={`btn btn-outline-secondary ${mineOnly ? 'active' : ''}`}
            onClick={() => setMineOnly(true)}
            aria-pressed={mineOnly}
          >
            My listings
          </button>
        </div>
      )}

      {embedded ? (
        <div className="camp-essentials-gear__location-banner mb-4">
          <div className="camp-essentials-gear__location-banner-main">
            <div className="camp-essentials-gear__location-icon" aria-hidden="true">
              <FaMapMarkerAlt size={16} />
            </div>
            <div>
              <p className="camp-essentials-gear__location-title">
                {selectedLocation
                  ? `Listings near ${locationQuery}`
                  : 'All marketplace listings'}
              </p>
              <p className="camp-essentials-gear__location-sub">
                {selectedLocation
                  ? `Within ${radius} miles`
                  : 'Add a location to filter by distance'}
              </p>
            </div>
          </div>
          <button
            type="button"
            className="y-btn y-btn-outline-secondary y-btn-sm"
            onClick={() => setLocationOpen((v) => !v)}
          >
            {locationOpen ? 'Hide location' : 'Filter by location'}
          </button>
        </div>
      ) : null}

      {(embedded ? locationOpen : true) && (
        <div className="community-listings-filters mb-3">
          <div className="row g-2">
            {!embedded && (
              <>
                <div className="col-12 col-md">
                  <input
                    type="search"
                    className="form-control form-control-sm"
                    placeholder="Search title or description"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    aria-label="Search listings"
                  />
                </div>
                <div className="col-12 col-md">
                  <select
                    className="form-select form-select-sm"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    aria-label="Category"
                  >
                    <option value="">All categories</option>
                    {LISTING_CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
            <div className="col-12 col-md">
              <PlaceSearchInput
                value={locationQuery}
                onChange={(v) => {
                  setLocationQuery(v);
                  if (!(v || '').trim()) setSelectedLocation(null);
                }}
                onSelect={(place) => {
                  setSelectedLocation(place);
                  setLocationQuery(place.label || place.name || '');
                  if (embedded) setLocationOpen(false);
                }}
                placeholder="City or area (e.g. Cullman, Alabama)"
                types={CITY_TYPES}
                aria-label="Search by location"
              />
            </div>
            <div className="col-12 col-md-auto">
              <label className="form-label form-label-sm me-1" htmlFor="listings-radius">
                Radius
              </label>
              <select
                id="listings-radius"
                className="form-select form-select-sm"
                value={radius}
                onChange={(e) => setRadius(Number(e.target.value))}
                aria-label="Radius in miles"
              >
                {RADIUS_CHOICES.map((mi) => (
                  <option key={mi} value={mi}>
                    {mi} mi
                  </option>
                ))}
              </select>
            </div>
          </div>
          {selectedLocation && (
            <div className="small text-muted mt-1">
              Showing listings within {radius} mi of {locationQuery}. Clear the location to see all.
            </div>
          )}
        </div>
      )}

      {error && <div className="alert alert-warning small mb-2">{error}</div>}
      {loading && <p className="text-muted small">Loading…</p>}

      {!loading && visibleListings.length === 0 && (
        <p className="camp-gear-finder__empty">
          {mineOnly
            ? 'You have no listings yet. Post one to sell or give away gear.'
            : 'No listings match. Post one to get started — list gear you want to sell or give away.'}
        </p>
      )}

      {!loading && visibleListings.length > 0 && (
        <div
          className={
            mockupCards ? 'community-listings-grid--mockup' : 'community-listings-grid'
          }
          data-testid="community-listings-grid"
        >
          {visibleListings.map((listing) => (
            <ListingCard
              key={listing.id}
              listing={listing}
              currentUserId={currentUserId}
              showStatusBadge={mineOnly}
              mockup={mockupCards}
            />
          ))}
        </div>
      )}
    </div>
  );
}
